/**
 * CodeDisplayBlock component for rendering code with syntax highlighting
 * Provides a copy button and theme-aware code display
 */
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { CodeBlock, dracula, github } from 'react-code-blocks';
import { toast } from 'sonner';
import { Theme } from '../../theme/lightTheme';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { motion } from 'framer-motion';
import { AiOutlineCheck, AiOutlineCopy } from 'react-icons/ai';
import { tr } from '../../translation';

/**
 * Props interface for the CodeDisplayBlock component
 */
interface ButtonCodeblockProps {
  /** The code to be displayed and potentially copied */
  code: string;
}

/**
 * Container for the code block with scroll capabilities
 */
const Container = styled.div`
  position: relative;
  margin: 1rem 0;
  overflow-x: scroll;
  display: flex;
  flex-direction: column;
  text-align: left;
`;

/**
 * Button for copying code to clipboard
 * Positioned in the top-right corner of the code block
 */
const CopyButton = styled(motion.button)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  height: 1.25rem;
  width: 1.25rem;
  background: none;
  border: none;
  .icon {
    height: 1rem;
    width: 1rem;
    transition: transform 0.3s ease-in-out;
  }
`;

/**
 * CodeDisplayBlock component that renders a code snippet with syntax highlighting
 * Includes a copy button for easy copying of the code
 *
 * @param code - The code string to display in the block
 * @returns A code block with syntax highlighting and copy functionality
 */
const CodeDisplayBlock = ({ code }: ButtonCodeblockProps) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const theme = useTheme() as Theme;
  const { isDarkMode } = useSelector((state: RootState) => state.settings);

  /**
   * Copies the code to clipboard and shows a success toast
   */
  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setIsCopied(true);
    toast.success(tr('chatPage.codeCopied'));
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  return (
    <Container>
      <CopyButton onClick={copyToClipboard}>
        {isCopied ? (
          <AiOutlineCheck className="icon" color={theme.text.grey} />
        ) : (
          <AiOutlineCopy className="icon" color={theme.text.grey} />
        )}
      </CopyButton>
      <CodeBlock
        codeContainerStyle={{
          whiteSpace: 'pre-line',
        }}
        text={code}
        showLineNumbers={false}
        theme={isDarkMode ? dracula : github}
      />
    </Container>
  );
};

export default CodeDisplayBlock;
